import "@/App.css";
import {
  User,
  Target,
  Search,
  Settings,
  Users,
  Gem,
  BarChart3,
  CreditCard,
  Smartphone,
  ShieldCheck,
  TrendingUp,
  Briefcase,
  Star,
  Award,
  Quote,
  Flag,
  Mail,
  Linkedin,
} from "lucide-react";

const GOLD = "#A98B5F";

const SectionLabel = ({ icon: Icon, children, dark = false, testId }) => (
  <div className="flex items-center gap-5" data-testid={testId}>
    <Icon
      strokeWidth={1.1}
      className="h-11 w-11 shrink-0"
      style={{ color: GOLD }}
      aria-hidden="true"
    />
    <span
      className={`text-sm font-semibold tracking-[0.28em] ${
        dark ? "text-ivory" : "text-ink"
      }`}
    >
      {children}
    </span>
  </div>
);

const Divider = ({ dark = false }) => (
  <div
    className={`hidden lg:block w-px self-stretch ${
      dark ? "bg-white/15" : "bg-line"
    }`}
    aria-hidden="true"
  />
);

const Hero = () => (
  <header className="relative overflow-hidden bg-ivory" data-testid="hero-section">
    <div className="absolute inset-x-0 top-0 z-20">
      <div className="mx-auto flex max-w-6xl items-start justify-between px-6 pt-7">
        <span
          className="font-serif text-3xl font-semibold tracking-tight text-ink"
          data-testid="brand-monogram"
        >
          DP<span style={{ color: GOLD }}>.</span>
        </span>
        <nav aria-label="Primary" data-testid="primary-nav">
          <ul className="flex items-center gap-5 sm:gap-9">
            {[
              ["ABOUT", "#about", "nav-about"],
              ["EXPERIENCE", "#experience", "nav-experience"],
              ["EXPERTISE", "#expertise", "nav-expertise"],
              ["CONNECT", "#connect", "nav-connect"],
            ].map(([label, href, id]) => (
              <li key={id}>
                <a
                  href={href}
                  data-testid={id}
                  className="text-[10px] font-semibold tracking-[0.22em] text-ink transition-colors duration-300 hover:text-gold sm:text-xs"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>

    <div className="mx-auto grid max-w-6xl lg:grid-cols-[1.05fr_0.95fr]">
      <div className="relative z-10 px-6 pb-14 pt-32 sm:pt-36 lg:py-44 lg:pr-16">
        <h1
          className="font-serif text-4xl font-medium leading-[1.05] text-ink sm:text-5xl lg:text-6xl"
          data-testid="hero-name"
        >
          Darshana
          <br />
          Pathare
        </h1>
        <div
          className="mt-7 h-px w-14"
          style={{ backgroundColor: GOLD }}
          aria-hidden="true"
        />
        <p
          className="mt-7 font-serif text-2xl font-light leading-snug text-ink/80 sm:text-[1.7rem]"
          data-testid="hero-tagline"
        >
          Understanding Risk.
          <br />
          Improving Systems.
          <br />
          Developing People.
        </p>
        <p
          className="mt-9 text-[11px] font-semibold tracking-[0.22em] text-ink sm:text-xs"
          data-testid="hero-role"
        >
          AVP – RBL BANK
          <span className="mx-3 text-gold" aria-hidden="true">
            |
          </span>
          BANKING &amp; FINANCIAL RISK PROFESSIONAL
        </p>
      </div>

      <div className="relative px-6 pb-16 lg:px-0 lg:pb-0">
        <div className="overflow-hidden rounded-t-full lg:absolute lg:bottom-0 lg:left-0 lg:top-24 lg:w-[calc(100%+((100vw-72rem)/2))]">
          <img
            src="/assets/founder.jpg"
            alt="Portrait of Darshana Pathare"
            data-testid="founder-photo"
            className="h-[26rem] w-full object-cover object-top sm:h-[32rem] lg:h-full"
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </div>
    </div>
  </header>
);

const About = () => (
  <section id="about" className="bg-ivory py-20 lg:py-24" data-testid="about-section">
    <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[240px_1px_1fr] lg:gap-12">
      <SectionLabel icon={User} testId="about-label">
        ABOUT
      </SectionLabel>
      <Divider />
      <div className="max-w-2xl space-y-6 text-base leading-relaxed text-soft" data-testid="about-content">
        <p>
          With 15 years of experience in banking, Darshana specialises in
          transaction monitoring, merchant acquiring, digital payments, and risk
          &amp; compliance.
        </p>
        <p>
          Currently an AVP at RBL Bank, she focuses on identifying risks,
          strengthening processes and ensuring regulatory and internal
          compliance.
        </p>
        <p>
          Her experience at Mswipe has also given her a strong understanding of
          the digital payments ecosystem.
        </p>
        <p>
          She believes in building strong teams, creating clarity in complex
          situations and continuously improving how things work.
        </p>
      </div>
    </div>
  </section>
);

const WhatIDo = () => {
  const items = [
    {
      icon: Search,
      title: "Understand Risk",
      body: "Identify transaction patterns and risks to strengthen financial risk management.",
      testId: "what-i-do-risk",
    },
    {
      icon: Settings,
      title: "Improve Systems",
      body: "Build stronger monitoring and compliance processes that improve efficiency and consistency.",
      testId: "what-i-do-systems",
    },
    {
      icon: Users,
      title: "Develop People",
      body: "Build collaborative, high-performing teams through trust, accountability and approachable leadership.",
      testId: "what-i-do-people",
    },
  ];
  return (
    <section className="bg-beige py-20 lg:py-24" data-testid="what-i-do-section">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[240px_1px_1fr] lg:gap-12">
        <SectionLabel icon={Target} testId="what-i-do-label">
          WHAT I DO
        </SectionLabel>
        <Divider />
        <div className="grid gap-12 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-line">
          {items.map(({ icon: Icon, title, body, testId }, i) => (
            <div
              key={title}
              className={i === 0 ? "sm:pr-10" : "sm:px-10"}
              data-testid={testId}
            >
              <Icon
                strokeWidth={1.1}
                className="h-9 w-9"
                style={{ color: GOLD }}
                aria-hidden="true"
              />
              <h3 className="mt-5 font-serif text-xl font-medium text-ink">
                {title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-soft">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Expertise = () => {
  const items = [
    { icon: BarChart3, label: "Transaction Monitoring", testId: "expertise-transaction-monitoring" },
    { icon: CreditCard, label: "Merchant Acquiring", testId: "expertise-merchant-acquiring" },
    { icon: Smartphone, label: "UPI & Digital Payments", testId: "expertise-upi-digital-payments" },
    { icon: ShieldCheck, label: "Risk & Compliance Monitoring", testId: "expertise-risk-compliance" },
    { icon: Users, label: "Team Leadership", testId: "expertise-team-leadership" },
    { icon: TrendingUp, label: "Process Improvement", testId: "expertise-process-improvement" },
  ];
  return (
    <section id="expertise" className="bg-ivory py-20 lg:py-24" data-testid="expertise-section">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[240px_1px_1fr] lg:gap-12">
        <SectionLabel icon={Gem} testId="expertise-label">
          EXPERTISE
        </SectionLabel>
        <Divider />
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-6 lg:gap-0 lg:divide-x lg:divide-line">
          {items.map(({ icon: Icon, label, testId }, i) => (
            <div
              key={label}
              className={`flex flex-col items-center text-center ${
                i === 0 ? "lg:pr-6" : "lg:px-6"
              }`}
              data-testid={testId}
            >
              <Icon
                strokeWidth={1.1}
                className="h-9 w-9"
                style={{ color: GOLD }}
                aria-hidden="true"
              />
              <span className="mt-4 text-sm leading-snug text-soft">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Experience = () => {
  const roles = [
    {
      company: "RBL Bank",
      title: "AVP – Transaction Monitoring",
      body: "Transaction monitoring, risk management, and regulatory and internal compliance.",
      testId: "experience-rbl-bank",
    },
    {
      company: "Mswipe",
      title: "Transaction Monitoring Analyst",
      body: "Experience across transaction monitoring and the digital payments ecosystem.",
      testId: "experience-mswipe",
    },
  ];
  return (
    <section id="experience" className="bg-ink py-20 lg:py-24" data-testid="experience-section">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[240px_1px_1fr] lg:gap-12">
        <SectionLabel icon={Briefcase} dark testId="experience-label">
          EXPERIENCE
        </SectionLabel>
        <Divider dark />
        <div className="relative max-w-2xl pl-8" data-testid="experience-timeline">
          <div
            className="absolute bottom-2 left-[5px] top-2 w-px bg-white/20"
            aria-hidden="true"
          />
          <div className="space-y-12">
            {roles.map(({ company, title, body, testId }) => (
              <div key={company} className="relative" data-testid={testId}>
                <span
                  className="absolute -left-8 top-1.5 h-3 w-3 rounded-full border-2 border-ink"
                  style={{ backgroundColor: GOLD }}
                  aria-hidden="true"
                />
                <h3 className="font-serif text-xl font-medium text-ivory">
                  {company}
                </h3>
                <p className="mt-1 text-sm italic text-gold">{title}</p>
                <p className="mt-3 text-sm leading-relaxed text-ivory/70">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Recognition = () => {
  const items = [
    {
      icon: Award,
      title: "Best Performer, 4 Consecutive Years",
      body: "Recognised for consistent performance and contribution.",
      testId: "recognition-best-performer",
    },
    {
      icon: TrendingUp,
      title: "Two Promotions in 4.5 Years",
      body: "Progressed through increasing levels of responsibility, reflecting strong performance and leadership capabilities.",
      testId: "recognition-promotions",
    },
  ];
  return (
    <section className="bg-ivory py-20 lg:py-24" data-testid="recognition-section">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[240px_1px_1fr] lg:gap-12">
        <SectionLabel icon={Star} testId="recognition-label">
          RECOGNITION
        </SectionLabel>
        <Divider />
        <div className="grid gap-12 sm:grid-cols-2 sm:gap-0 sm:divide-x sm:divide-line">
          {items.map(({ icon: Icon, title, body, testId }, i) => (
            <div
              key={title}
              className={`flex gap-5 ${i === 0 ? "sm:pr-12" : "sm:px-12"}`}
              data-testid={testId}
            >
              <Icon
                strokeWidth={1.1}
                className="h-10 w-10 shrink-0"
                style={{ color: GOLD }}
                aria-hidden="true"
              />
              <div>
                <h3 className="text-base font-semibold text-ink">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-soft">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Philosophy = () => (
  <section className="bg-ivory pb-20 lg:pb-24" data-testid="philosophy-section">
    <div className="mx-auto grid max-w-6xl gap-16 px-6 lg:grid-cols-2 lg:gap-24">
      <div data-testid="leadership-philosophy">
        <SectionLabel icon={Quote} testId="philosophy-label">
          LEADERSHIP PHILOSOPHY
        </SectionLabel>
        <p className="mt-8 font-serif text-xl font-light italic leading-relaxed text-ink">
          Clarity creates confidence.
          <br />
          Strong systems create consistency.
          <br />
          Great people create impact.
        </p>
        <div className="mt-8 h-px w-24 bg-line" aria-hidden="true" />
        <p className="mt-8 max-w-md text-sm leading-relaxed text-soft">
          Darshana combines analytical thinking with an approachable,
          people-first leadership style.
        </p>
      </div>
      <div data-testid="looking-ahead">
        <SectionLabel icon={Flag} testId="looking-ahead-label">
          LOOKING AHEAD
        </SectionLabel>
        <div className="mt-8 max-w-md space-y-5 text-sm leading-relaxed text-soft">
          <p>
            Darshana aspires to grow into a larger leadership role, using her
            experience in transaction monitoring and digital payments to drive
            stronger processes, develop high-performing teams and contribute to
            strategic decision-making.
          </p>
          <p>
            Her goal is to continue learning, broaden her expertise and create
            meaningful impact.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Connect = () => (
  <footer id="connect" className="bg-ink py-16 lg:py-20" data-testid="connect-section">
    <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[240px_1px_1fr] lg:gap-12">
      <SectionLabel icon={Mail} dark testId="connect-label">
        CONNECT
      </SectionLabel>
      <Divider dark />
      <div className="space-y-5">
        <a
          href="https://linkedin.com/in/darshana-khandke-822121165"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="connect-linkedin"
          className="group flex w-fit items-center gap-4 text-sm text-ivory/80 transition-colors duration-300 hover:text-ivory"
        >
          <Linkedin
            strokeWidth={1.1}
            className="h-6 w-6"
            style={{ color: GOLD }}
            aria-hidden="true"
          />
          <span className="underline decoration-white/25 underline-offset-4 group-hover:decoration-gold">
            linkedin.com/in/darshana-khandke-822121165
          </span>
        </a>
        <a
          href="mailto:darshanakhandke@gmail.com"
          data-testid="connect-email"
          className="group flex w-fit items-center gap-4 text-sm text-ivory/80 transition-colors duration-300 hover:text-ivory"
        >
          <Mail
            strokeWidth={1.1}
            className="h-6 w-6"
            style={{ color: GOLD }}
            aria-hidden="true"
          />
          <span className="underline decoration-white/25 underline-offset-4 group-hover:decoration-gold">
            darshanakhandke@gmail.com
          </span>
        </a>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="min-h-screen bg-ivory font-sans text-ink antialiased">
      <Hero />
      <main>
        <About />
        <WhatIDo />
        <Expertise />
        <Experience />
        <Recognition />
        <Philosophy />
      </main>
      <Connect />
    </div>
  );
}

export default App;
